import { CSSProperties } from 'react';
import { Easing, interpolate } from 'remotion';

import { CLAMP_RIGHT } from '../constants';

interface StepSchedule {
  index: number;
  start: number;
  end: number;
}

/**
 * Calculates flip animation schedule with uniform distribution
 * @param targetValue - Final value to count to
 * @param totalFrames - Total animation duration in frames
 * @param startFrame - Frame to begin animation
 * @returns Array of step schedules with frame ranges
 */
export const calculateFlipSchedule = (
  targetValue: number,
  totalFrames: number,
  startFrame: number,
): StepSchedule[] => {
  if (targetValue <= 0) return [];

  // Create schedule array where each step gets equal frame duration
  // Example: targetValue=3, totalFrames=30 → each step gets 10 frames
  return Array.from({ length: targetValue }, (_, i) => {
    const progressStart = i / targetValue;
    const progressEnd = (i + 1) / targetValue;

    return {
      index: i,
      start: startFrame + Math.floor(progressStart * totalFrames),
      end: startFrame + Math.ceil(progressEnd * totalFrames),
    };
  });
};

/**
 * Finds the active step and calculates flip progress for current frame
 */
export const getFlipState = (
  frame: number,
  startFrame: number,
  totalFrames: number,
  targetValue: number,
  schedule: StepSchedule[],
): {
  currentValue: number;
  nextValue: number;
  flipProgress: number;
} => {
  // Before animation starts or invalid target
  if (targetValue <= 0 || frame < startFrame) {
    return { currentValue: 0, nextValue: 0, flipProgress: 0 };
  }

  // Animation completed - show final value
  if (frame >= startFrame + totalFrames) {
    return {
      currentValue: targetValue,
      nextValue: targetValue,
      flipProgress: 0,
    };
  }

  // Find which step is active at current frame
  const activeStep = schedule.find((s) => frame >= s.start && frame < s.end);

  // If no active step found, animation is complete (frame is after last step)
  if (!activeStep) {
    return {
      currentValue: targetValue,
      nextValue: targetValue,
      flipProgress: 0,
    };
  }

  // Calculate flip progress within current step (0 → 1)
  const stepDuration = Math.max(1, activeStep.end - activeStep.start);
  const rawProgress = (frame - activeStep.start) / stepDuration;

  return {
    currentValue: activeStep.index,
    nextValue: activeStep.index + 1,
    // Apply easing to make flip animation smooth
    flipProgress: Easing.inOut(Easing.ease)(rawProgress),
  };
};

/** Shared fill progress + gradient position (diagonal top-left → bottom-right) */
const getFillProgress = (
  currentFrame: number,
  startFrame: number,
  fillDuration: number,
): { fillProgress: number; gradientPosition: number } | undefined => {
  const localFrame = Math.max(0, currentFrame - startFrame);
  const fillProgress = interpolate(
    localFrame,
    [0, fillDuration],
    [0, 1],
    CLAMP_RIGHT,
  );
  if (fillProgress <= 0) return undefined;

  const gradientPosition = interpolate(fillProgress, [0, 1], [-50, 150]);
  return { fillProgress, gradientPosition };
};

export const getTextFillStyle = (
  currentFrame: number,
  startFrame: number,
  fillDuration: number,
  fillColor: string,
): CSSProperties | undefined => {
  const fill = getFillProgress(currentFrame, startFrame, fillDuration);
  if (!fill) return undefined;

  const { fillProgress, gradientPosition } = fill;
  const gradient =
    fillProgress >= 0.99
      ? `linear-gradient(${fillColor}, ${fillColor})`
      : `linear-gradient(135deg, ${fillColor} ${gradientPosition}%, white ${gradientPosition + 30}%)`;

  return {
    color: 'transparent',
    backgroundImage: gradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };
};

export const getBackgroundFillStyle = (
  currentFrame: number,
  startFrame: number,
  fillDuration: number,
  fillColor: string,
  baseColor: string,
): CSSProperties | undefined => {
  const fill = getFillProgress(currentFrame, startFrame, fillDuration);
  if (!fill) return undefined;

  const { fillProgress, gradientPosition } = fill;
  const gradient =
    fillProgress >= 0.99
      ? `linear-gradient(${fillColor}, ${fillColor})`
      : `linear-gradient(135deg, ${fillColor} ${gradientPosition}%, ${baseColor} ${gradientPosition + 30}%)`;

  return { backgroundImage: gradient, backgroundColor: 'transparent' };
};
